import React, { useEffect, useMemo, useState } from "react";
import fetchData from "../api/fetchData";

/* Column order is shared by the table and the CSV export. */
const COLUMNS = [
  { key: "createdAtFormatted", label: "Date" },
  { key: "email", label: "Email" },
  { key: "campaign", label: "Campaign" },
  { key: "store", label: "Store" },
  { key: "couponTitle", label: "Coupon / Deal" },
  { key: "couponCode", label: "Code" },
  { key: "category", label: "Category" },
  { key: "source", label: "Source" },
  { key: "language", label: "Lang" },
  { key: "pageUrl", label: "Page URL" },
  { key: "offerId", label: "Offer ID" },
];

/* RFC-4180-ish CSV cell escaping. */
const csvCell = (value) => {
  const str = value == null ? "" : String(value);
  return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
};

const SubscribersPage = () => {
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");

  const loadSubscribers = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchData("/admin/subscriptions");
      setSubscribers(Array.isArray(data) ? data : []);
    } catch (err) {
      setError("Failed to load subscribers. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSubscribers();
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return subscribers;
    return subscribers.filter((s) =>
      COLUMNS.some((c) => String(s[c.key] || "").toLowerCase().includes(q))
    );
  }, [subscribers, search]);

  const downloadCsv = () => {
    const header = COLUMNS.map((c) => csvCell(c.label)).join(",");
    const rows = filtered.map((s) =>
      COLUMNS.map((c) => csvCell(s[c.key])).join(",")
    );
    const csv = [header, ...rows].join("\r\n");
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `subscribers-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-6 md:p-10 max-sm:pt-16">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Email Subscribers</h1>
          <p className="text-sm text-gray-500 mt-1">
            Visitors who asked to be alerted about new coupons & deals.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-gray-600">
            {filtered.length} of {subscribers.length}
          </span>
          <button
            onClick={loadSubscribers}
            className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg text-sm font-medium transition-colors"
          >
            Refresh
          </button>
          <button
            onClick={downloadCsv}
            disabled={filtered.length === 0}
            className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
          >
            Download CSV
          </button>
        </div>
      </div>

      <input
        type="text"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search by email, campaign, store, coupon…"
        className="w-full max-w-md mb-4 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
      />

      {error && (
        <div className="mb-4 px-4 py-3 bg-red-100 text-red-700 rounded-lg text-sm">
          {error}
        </div>
      )}

      <div className="overflow-x-auto bg-white rounded-xl shadow">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-800 text-white">
            <tr>
              {COLUMNS.map((c) => (
                <th
                  key={c.key}
                  className="px-4 py-3 text-left font-semibold whitespace-nowrap"
                >
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? (
              <tr>
                <td colSpan={COLUMNS.length} className="px-4 py-8 text-center text-gray-500">
                  Loading…
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={COLUMNS.length} className="px-4 py-8 text-center text-gray-500">
                  No subscribers yet.
                </td>
              </tr>
            ) : (
              filtered.map((s, idx) => (
                <tr key={s.id || idx} className="hover:bg-gray-50">
                  {COLUMNS.map((c) => (
                    <td
                      key={c.key}
                      className="px-4 py-3 text-gray-700 max-w-[260px] truncate"
                      title={s[c.key] || ""}
                    >
                      {c.key === "pageUrl" && s[c.key] ? (
                        <a
                          href={s[c.key]}
                          target="_blank"
                          rel="noreferrer"
                          className="text-blue-600 hover:underline"
                        >
                          {s[c.key]}
                        </a>
                      ) : (
                        s[c.key] || "—"
                      )}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SubscribersPage;
